import { sha1 } from 'object-hash';
import { BadRequestError } from '@cian/valuation-utils-component';

import { TThunkAction } from '../../types/redux';
import { EDealType } from '../../types/dealType';
import { prepareSuburbanResponse } from './utils/prepareResponse';
import {
  fetchGetEstimationSuburban,
  IGetEstimationSuburbanResponse200 as IRes200,
  IGetEstimationSuburbanResponse400 as IRes400,
  TGetEstimationSuburbanResponse,
} from '../../repositories/price-estimator/v1/get-estimation-suburban';
import { getEstimationFailure, getEstimationSuccess, IGetEstimationSuccessPayload } from './actions';
import { selectSuburbanApiParameters } from '../../selectors/estimation';
import { setPreciseFiltersState } from '../preciseFilters';
import { preparePreciseFilters } from '../preciseFilters/utils';
import { clearIsFromOfferCard } from '../page';
import { successAction as setEstimationChartData } from '../estimationChart';

/**
 * Процесс в санке:
 * 1. Диспатч начала запроса за оценкой
 * 2. Выполнение запроса за оценкой с переданными параметрами
 * 3. Обработка ошибки: логирование, диспатч ошибки, возврат в случае ошибки
 * 4. Диспатч успешного ответа (значение оценки и истории изменения оценки) и его возврат
 * */
export const getEstimationSuburban =
  (): TThunkAction<Promise<IGetEstimationSuccessPayload | Error | BadRequestError>> =>
  async (dispatch, getState, context) => {
    const dealType = EDealType.Sale;
    const state = getState();
    const { estimation } = state;
    const currentHash = estimation[dealType].filtersHash;
    const options = {
      parameters: selectSuburbanApiParameters(state),
      httpApi: context.httpApi,
    };

    let res: TGetEstimationSuburbanResponse;

    try {
      res = await fetchGetEstimationSuburban<IRes200, IRes400>(options);

      if (res.statusCode !== 200) {
        throw new BadRequestError({
          domain: 'getEstimationSuburban',
          message: res.response.message as string,
          details: { errors: JSON.stringify(res.response.errors) },
        });
      }
    } catch (e) {
      if (!(e instanceof BadRequestError)) {
        context.logger.error(e, {
          message: 'Ошибка при загрузке оценки загородки',
          parameters: JSON.stringify(options.parameters),
        });
      } else {
        context.logger.debug('getEstimationSuburban: bad request', {
          message: e.message,
          domain: e.domain,
          parameters: JSON.stringify(options.parameters),
        });
      }

      dispatch(getEstimationFailure({ dealType }));

      return e;
    }

    const filtersHash = sha1(options.parameters);
    if (currentHash && currentHash !== filtersHash) {
      dispatch(clearIsFromOfferCard());
    }

    const data = prepareSuburbanResponse(res, dealType);

    if (res.response.filtersFromDataPayment) {
      const preciseFilters = preparePreciseFilters(res.response.filtersFromDataPayment);

      dispatch(setPreciseFiltersState(preciseFilters));
    }

    dispatch(setEstimationChartData(res.response.history || null));

    const result = {
      dealType,
      data,
      filtersHash,
    };

    dispatch(getEstimationSuccess(result));

    return result;
  };
